import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApiService from '../services/ApiService';
import Navigation from '../components/Navigation';
import Avatar from '../components/Avatar';
import UserProfileModal from '../components/UserProfileModal';
import './HashtagBrowse.css';

interface Hashtag {
  id: number;
  name: string;
  displayName: string;
  category: string | null;
  isOfficial: boolean;
  userCount: number;
}

interface User {
  id: number;
  name?: string;
  email: string;
  distance?: number | null;
  profile?: {
    bio?: string;
    age?: number;
    location?: string;
    interests?: string;
    photoUrl?: string;
    photos?: Array<{ id: number; url: string; order: number }>;
    variant?: {
      name: string;
      description?: string;
    };
  };
  mood?: {
    mood: string;
    expiresAt: string;
  } | null;
  greenFlags?: Array<{
    flag: {
      id: number;
      name: string;
      emoji?: string;
      description: string;
      category: string;
    };
    verifiedBy?: number;
  }>;
}

const HashtagBrowse: React.FC = () => {
  const { hashtagId } = useParams<{ hashtagId?: string }>();
  const navigate = useNavigate();
  const [hashtags, setHashtags] = useState<Hashtag[]>([]);
  const [selectedHashtag, setSelectedHashtag] = useState<Hashtag | null>(null);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [usersLoading, setUsersLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  const categories = [
    { value: 'all', label: 'All' },
    { value: 'interests', label: 'Interests' },
    { value: 'lifestyle', label: 'Lifestyle' },
    { value: 'looking_for', label: 'Looking For' },
    { value: 'personality', label: 'Personality' },
    { value: 'physical', label: 'Physical' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchHashtags();
  }, []);

  useEffect(() => {
    if (hashtagId && hashtags.length > 0) {
      const hashtag = hashtags.find(h => h.id === parseInt(hashtagId));
      if (hashtag) {
        handleHashtagClick(hashtag);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hashtagId, hashtags]);

  const fetchHashtags = async () => {
    try {
      const response = await ApiService.get('/hashtags', {
        params: { limit: 200 }
      });
      setHashtags(response.data.hashtags);
    } catch (error) {
      console.error('Failed to fetch hashtags:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsersByHashtag = async (hashtagId: number) => {
    setUsersLoading(true);
    try {
      const response = await ApiService.get(`/hashtags/${hashtagId}/users`);
      setUsers(response.data.users);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleHashtagClick = (hashtag: Hashtag) => {
    setSelectedHashtag(hashtag);
    fetchUsersByHashtag(hashtag.id);
  };

  const handleBack = () => {
    setSelectedHashtag(null);
    setUsers([]);
    setSelectedUser(null);
    navigate('/hashtags');
  };

  const handleLike = async () => {
    if (!selectedUser || actionLoading) return;

    setMessage('');
    setActionLoading(true);

    try {
      const response = await ApiService.post(`/likes/${selectedUser.id}`);

      if (response.data.match) {
        setMessage(`🎉 It's a match with ${selectedUser.name || 'this user'}!`);
        setTimeout(() => setMessage(''), 3000);
      } else {
        setMessage(`✅ Liked ${selectedUser.name || 'this user'}!`);
        setTimeout(() => setMessage(''), 2000);
      }

      setUsers(users.filter(u => u.id !== selectedUser.id));
      setSelectedUser(null);
    } catch (error: any) {
      console.error('Failed to like user:', error);

      let errorMsg = 'Failed to like user. Please try again.';

      if (error.response?.data) {
        const data = error.response.data;
        if (data.error === 'Daily like limit reached' || data.upgradeRequired) {
          errorMsg = `Daily like limit reached (${data.used}/${data.limit}). Upgrade to get more likes!`;
        } else if (data.message) {
          errorMsg = data.message;
        } else if (data.error) {
          errorMsg = data.error;
        }
      }

      setMessage(`❌ ${errorMsg}`);
      setTimeout(() => setMessage(''), 8000);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePass = () => {
    if (!selectedUser) return;
    setUsers(users.filter(u => u.id !== selectedUser.id));
    setSelectedUser(null);
  };

  const getFilteredHashtags = () => {
    let filtered = hashtags;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(h => h.category === selectedCategory);
    }

    if (searchQuery) {
      filtered = filtered.filter(h =>
        h.name.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered;
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="hashtag-browse-container">
          <p>Loading...</p>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="hashtag-browse-container">
        <h1>Browse by Hashtag</h1>

        {message && <div className="match-message">{message}</div>}

        {!selectedHashtag ? (
          <>
            <div className="hashtag-filters">
              <input
                type="text"
                className="hashtag-search"
                placeholder="Search hashtags..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />

              <select
                className="hashtag-category-select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="hashtags-grid">
              {getFilteredHashtags().map((hashtag) => (
                <button
                  key={hashtag.id}
                  className={`hashtag-card ${hashtag.isOfficial ? 'official' : 'custom'}`}
                  onClick={() => handleHashtagClick(hashtag)}
                >
                  <span className="hashtag-name">{hashtag.displayName}</span>
                  <span className="hashtag-user-count">
                    {hashtag.userCount} {hashtag.userCount === 1 ? 'user' : 'users'}
                  </span>
                  {hashtag.category && (
                    <span className="hashtag-category-badge">
                      {hashtag.category.replace('_', ' ')}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {getFilteredHashtags().length === 0 && (
              <div className="no-results">
                <p>No hashtags found matching your search.</p>
              </div>
            )}
          </>
        ) : (
          <>
            <div className="hashtag-header">
              <button onClick={handleBack} className="back-button">
                ← Back to Hashtags
              </button>
              <h2>{selectedHashtag.displayName}</h2>
              <p className="user-count-text">
                {users.length} {users.length === 1 ? 'user' : 'users'} found
              </p>
            </div>

            {usersLoading ? (
              <p>Loading users...</p>
            ) : users.length > 0 ? (
              <div className="users-grid">
                {users.map((user) => (
                  <div
                    key={user.id}
                    className="user-card-preview"
                    onClick={() => setSelectedUser(user)}
                  >
                    <div className="card-photo">
                      <Avatar
                        photoUrl={user.profile?.photoUrl}
                        name={user.name}
                        size="large"
                      />
                    </div>

                    <div className="card-info">
                      <h3>
                        {user.name || 'Anonymous'}
                        {user.profile?.age && <span className="age">, {user.profile.age}</span>}
                      </h3>

                      {user.profile?.variant && (
                        <div className="variant-badge-small">
                          {user.profile.variant.name}
                        </div>
                      )}

                      {user.profile?.location && (
                        <p className="location-small">
                          📍 {user.profile.location}
                          {user.distance && ` • ${user.distance}mi`}
                        </p>
                      )}

                      {user.greenFlags && user.greenFlags.length > 0 && (
                        <div className="flags-preview">
                          {user.greenFlags.slice(0, 3).map((userFlag) => (
                            <span key={userFlag.flag.id} className="flag-emoji">
                              {userFlag.flag.emoji}
                            </span>
                          ))}
                          {user.greenFlags.length > 3 && (
                            <span className="flag-more">+{user.greenFlags.length - 3}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-users">
                <p>No users found with this hashtag.</p>
              </div>
            )}
          </>
        )}
      </div>

      {selectedUser && (
        <UserProfileModal
          user={selectedUser}
          onClose={() => setSelectedUser(null)}
          onLike={handleLike}
          onPass={handlePass}
          actionLoading={actionLoading}
        />
      )}
    </>
  );
};

export default HashtagBrowse;
